import React from 'react';
import { Helmet } from 'react-helmet-async';

// Define types for SEO component props
interface SEOProps {
  /**
   * Page title - will be appended with " | Meduler"
   */
  title?: string;
  
  /**
   * Page meta description
   */
  description?: string;
  
  /**
   * Comma-separated keywords
   */
  keywords?: string;
  
  /**
   * URL for Open Graph image
   */
  image?: string;
  
  /**
   * Content type - 'website' for most pages, 'article' for blog posts
   */
  type?: 'website' | 'article';
  
  /**
   * Canonical URL
   */
  canonicalUrl?: string;
  
  /**
   * Structured data for the page (JSON-LD)
   */
  structuredData?: any;
  
  /**
   * Page-specific schema type
   */
  schemaType?: 'SoftwareApplication' | 'MedicalWebPage' | 'FAQPage' | 'HowTo';
  
  /**
   * Data for generating schema based on schema type
   */
  schemaData?: any;
}

/**
 * SEOHead Component
 * 
 * This component handles all meta tags, Open Graph, Twitter Card, and structured data
 * for enhanced search engine visibility and social media sharing.
 * 
 * @example
 * ```jsx
 * <SEOHead 
 *   title="Medication Dashboard" 
 *   description="View and manage all your medications in one place"
 *   schemaType="MedicalWebPage"
 *   schemaData={{ 
 *     topic: "Medication Management", 
 *     summary: "Dashboard for managing medications", 
 *     lastReviewed: "2025-03-15" 
 *   }}
 * />
 * ```
 */
const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  canonicalUrl,
  structuredData,
  schemaType,
  schemaData,
}) => {
  // Default values for SEO properties
  const defaultTitle = "Smart Medication Management | Meduler";
  const defaultDescription = "Effortlessly manage your medications with Meduler. Intelligent scheduling, seamless sharing, and intuitive medication tracking.";
  const defaultKeywords = "medication management, smart pill tracker, digital health assistant, medication reminder app, health tech, medication organizer";
  // Use absolute URL for default image
  const siteUrl = "https://www.meduler.com";
  const defaultImage = `${siteUrl}/social/og-image.png`;
  const defaultImageWidth = 1200;
  const defaultImageHeight = 630;
  const defaultImageType = "image/png";
  
  // Format the title with the Meduler brand name
  const formattedTitle = title ? `${title} | Meduler` : defaultTitle;
  
  // Generate structured data based on type
  const generateStructuredData = () => {
    if (structuredData) {
      return structuredData;
    }
    
    if (!schemaType) {
      return null;
    }
    
    switch(schemaType) {
      case 'SoftwareApplication':
        return {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Meduler',
          'applicationCategory': 'HealthApplication',
          'operatingSystem': 'Web, iOS, Android',
          'offers': Array.isArray(schemaData?.pricing) 
            ? schemaData.pricing.map((offer: any) => ({
                '@type': 'Offer',
                'price': offer.price,
                'priceCurrency': 'USD',
                'description': offer.description
              }))
            : {
                '@type': 'Offer',
                'price': '0',
                'priceCurrency': 'USD',
                'description': 'Free basic medication management'
              },
          ...(schemaData?.rating && {
            'aggregateRating': {
              '@type': 'AggregateRating',
              'ratingValue': schemaData.rating.value,
              'ratingCount': schemaData.rating.count
            }
          })
        };
      
      case 'MedicalWebPage':
        return {
          '@context': 'https://schema.org',
          '@type': 'MedicalWebPage',
          'headline': title || 'Medication Management',
          'about': {
            '@type': 'MedicalEntity',
            'name': schemaData?.topic || 'Medication Management'
          },
          'mainContentOfPage': schemaData?.summary || description || defaultDescription,
          'lastReviewed': schemaData?.lastReviewed || new Date().toISOString().split('T')[0]
        };
      
      case 'FAQPage':
        return {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': Array.isArray(schemaData?.faqs) 
            ? schemaData.faqs.map((faq: any) => ({
                '@type': 'Question',
                'name': faq.question,
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': faq.answer
                }
              }))
            : []
        };
      
      case 'HowTo':
        return {
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          'name': schemaData?.name || title || 'How to use Meduler',
          'description': schemaData?.description || description || defaultDescription,
          'step': Array.isArray(schemaData?.steps) 
            ? schemaData.steps.map((step: any, index: number) => ({
                '@type': 'HowToStep',
                'position': index + 1,
                'name': step.name,
                'text': step.text,
                ...(step.image && { 'image': step.image })
              }))
            : []
        };
      
      default:
        return null;
    }
  };
  
  const jsonLd = generateStructuredData();
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{formattedTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph Tags - Enhanced for better preview */}
      <meta property="og:title" content={title || defaultTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:image" content={image || defaultImage} />
      <meta property="og:image:width" content={`${defaultImageWidth}`} />
      <meta property="og:image:height" content={`${defaultImageHeight}`} />
      <meta property="og:image:type" content={defaultImageType} />
      <meta property="og:image:alt" content={description || defaultDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Meduler" />
      <meta property="og:locale" content="en_US" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {!canonicalUrl && <meta property="og:url" content={siteUrl} />}
      
      {/* Twitter Card Tags - Enhanced for better preview */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || defaultTitle} />
      <meta name="twitter:description" content={description || defaultDescription} />
      <meta name="twitter:image" content={image || defaultImage} />
      <meta name="twitter:image:alt" content={description || defaultDescription} />
      <meta name="twitter:site" content="@meduler" />
      <meta name="twitter:creator" content="@meduler" />
      <meta name="twitter:domain" content="meduler.com" />
      
      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
